import {useState} from 'react';

import {useCreateAccessCodesMutation} from '~/store/rtkQuery';
import {AuditLogButton} from '~/features/AuditLog/AuditLogButton';

const ENTITLEMENT = 'LUNA_ALLACCESS_V1';

export const TrialCodes = () => {
  const [createAccessCodes, {isLoading, error}] =
    useCreateAccessCodesMutation();

  const [client, setClient] = useState('');
  const [notes, setNotes] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [quantity, setQuantity] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [createdCodes, setCreatedCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const canSubmit = !!durationDays && !!quantity && Number(quantity) > 0;

  const onSubmit = async () => {
    setSuccessMessage('');
    setCreatedCodes([]);
    setCopied(false);

    const response = await createAccessCodes({
      client: client.trim() || null,
      notes: notes.trim() || null,
      entitlement: ENTITLEMENT,
      durationDays: Number(durationDays),
      quantity: Number(quantity),
    });

    if (!('error' in response)) {
      setSuccessMessage(
        `Created ${quantity} access code(s)${client.trim() ? ` for ${client.trim()}` : ''}.`,
      );
      setCreatedCodes(response.data.redemptionCodes);
      setClient('');
      setNotes('');
      setDurationDays('');
      setQuantity('');
    }
  };

  const onCopyAll = async () => {
    await navigator.clipboard.writeText(createdCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="p-5">
      <h1 className="text-2xl font-semibold">Teen Premium Trial Codes</h1>
      <p className="mt-2 text-sm text-gray-600">
        Generate direct-access membership codes (e.g. for partnerships, gifting,
        or giveaways).
      </p>

      <div className="mt-4 max-w-md flex flex-col gap-3">
        <div className="form-control">
          <label className="label" htmlFor="entitlement">
            <span className="label-text font-medium">Entitlement</span>
          </label>
          <input
            id="entitlement"
            className="input input-bordered w-full"
            type="text"
            value={ENTITLEMENT}
            disabled
          />
        </div>

        <div className="form-control">
          <label className="label" htmlFor="duration-days">
            <span className="label-text font-medium">Duration (days)</span>
          </label>
          <input
            id="duration-days"
            className="input input-bordered w-full"
            type="number"
            placeholder="30"
            value={durationDays}
            onChange={e => setDurationDays(e.target.value)}
          />
        </div>

        <div className="form-control">
          <label className="label" htmlFor="client">
            <span className="label-text font-medium">Client</span>
          </label>
          <input
            id="client"
            className="input input-bordered w-full"
            type="text"
            placeholder="Lil-lets"
            value={client}
            onChange={e => setClient(e.target.value)}
          />
        </div>

        <div className="form-control">
          <label className="label" htmlFor="notes">
            <span className="label-text font-medium">Notes</span>
          </label>
          <input
            id="notes"
            className="input input-bordered w-full"
            type="text"
            placeholder="10 monthly memberships for giveaway"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <div className="form-control">
          <label className="label" htmlFor="quantity">
            <span className="label-text font-medium">Quantity</span>
          </label>
          <input
            id="quantity"
            className="input input-bordered w-full"
            type="number"
            placeholder="10"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
          />
        </div>

        <button
          className={`btn mt-2 ${
            canSubmit && !isLoading ? 'btn-primary' : 'btn-outline'
          }`}
          onClick={onSubmit}
          disabled={!canSubmit || isLoading}>
          {isLoading ? 'Creating...' : 'Create'}
        </button>

        {!!error && (
          <div role="alert" className="alert alert-error">
            <span>{error.message}</span>
          </div>
        )}

        {!!successMessage && (
          <div role="alert" className="alert alert-success">
            <span>{successMessage}</span>
          </div>
        )}

        {createdCodes.length > 0 && (
          <div className="form-control">
            <label className="label" htmlFor="created-codes">
              <span className="label-text font-medium">Codes</span>
            </label>
            <textarea
              id="created-codes"
              className="textarea textarea-bordered font-mono"
              rows={Math.min(createdCodes.length, 10)}
              readOnly
              value={createdCodes.join('\n')}
            />
            <button className="btn btn-outline mt-2" onClick={onCopyAll}>
              {copied ? 'Copied!' : 'Copy all'}
            </button>
          </div>
        )}
      </div>

      <AuditLogButton endpoints={['POST /subscriptions/accessCodes']} />
    </div>
  );
};
